import { BootstrapGridBreakpoints } from '@gn-audio/ui/constants/bootstrap-grid-breakpoints';
import { smoothScroll } from '@gn-audio/ui/tools/smooth-srcoll';
import { element, isFunction, isObject } from 'angular';
import { Directive, Input, OnInit, Output } from 'angular-ts-decorators';

import { IAnchorBarItem } from '../models';
import { IAnchorBarService } from '../services';
import { debounce } from '../utils/debounce';

import template from './anchor-bar.directive.html';

interface IAnchorBarScope extends ng.IScope {
  anchors: IAnchorBarItem[];
  hidden: boolean;
  loading: boolean;
  mobile: boolean;

  select(anchor: IAnchorBarItem): void;
}

@Directive({
  restrict: 'E',
  selector: 'anchorBar',
  template,
})
export class AnchorBarDirective implements OnInit {
  public static $inject: ReadonlyArray<string> = ['$scope', '$element', 'anchorBarService'];

  @Input('@') public makeTabs?: string;
  @Input('=') public highlight?: boolean;
  @Input('=') public scrollOffset?: number;

  @Output() public onMenuToggleState?: () => void;

  constructor(
    private $scope: IAnchorBarScope,
    private $element: JQLite,
    private anchorBarService: IAnchorBarService,
  ) { }

  public ngOnInit() {
    this.$scope.anchors = [];
    this.$scope.loading = true;
    this.$scope.mobile = document.documentElement.offsetWidth < BootstrapGridBreakpoints.Medium;

    const scrollHandler = debounce(() => {
      anchorHighlight();
    }, 200);

    const condensedMenu: JQuery = element('.condensed-menu__content');
    const menuHeight = condensedMenu.length > 0 ? condensedMenu[0].clientHeight : 0;
    const anchorBarOpenClass = 'anchor-bar__link--open';
    const anchorHighlight = () => {
      if (this.$scope.anchors.length === 0) {
        return;
      }

      this.$scope.anchors.forEach((anchor: IAnchorBarItem) => {
        const $anchorOwner: JQuery = element(`#${anchor.id}`);
        if ($anchorOwner.length === 0) {
          return;
        }

        const offsetTop: number = Math.ceil($anchorOwner.offset().top) - 1;
        const elementOffset = offsetTop - menuHeight;
        const anchorElement: JQuery = element(`a[title="${anchor.title}"]`, this.$element);
        if (
          elementOffset <= window.pageYOffset &&
          elementOffset + document.getElementById(anchor.id).scrollHeight > window.pageYOffset
        ) {
          anchorElement.addClass(anchorBarOpenClass);
        } else {
          anchorElement.removeClass(anchorBarOpenClass);
        }
      });
    };

    if (this.highlight) {
      document.addEventListener('scroll', scrollHandler);
    }

    this.$scope.$on('$destroy', () => {
      document.removeEventListener('scroll', scrollHandler);
    });

    this.$scope.select = (anchor: IAnchorBarItem) => {
      if (isFunction(this.onMenuToggleState) && document.documentElement.offsetWidth < BootstrapGridBreakpoints.Medium) {
        this.onMenuToggleState();
      }

      if (!isObject(anchor)) {
        return;
      }

      if (!this.$scope.anchors.some((a: IAnchorBarItem): boolean => a.id === anchor.id)) {
        return;
      }

      if (anchor.selected) {
        return;
      }

      if (this.makeTabs === undefined) {
        smoothScroll(document.getElementById(anchor.id), {
          offset: this.scrollOffset || 0,
        });
      } else {
        this.$scope.anchors.forEach((a: IAnchorBarItem) => {
          a.selected = false;
          element('#' + a.id)
            .removeClass('anchor-bar-tab--selected')
            .addClass('anchor-bar-tab--hidden');
        });
        anchor.selected = true;
        element('#' + anchor.id)
          .removeClass('anchor-bar-tab--hidden')
          .addClass('anchor-bar-tab--selected');
        window.scrollTo(window.pageXOffset, window.pageYOffset + 1);
        window.scrollTo(window.pageXOffset, window.pageYOffset - 1);
      }
    };

    this.$scope.$watch(
      (): IAnchorBarItem[] => this.anchorBarService.getAnchors(),
      () => {
        this.$scope.anchors = this.anchorBarService.getAnchors();
        this.$scope.loading = false;
        if (!this.$scope.mobile && this.makeTabs !== undefined) {
          this.$scope.select(this.$scope.anchors[0]);
        }
      },
    );
  }
}
